/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 */
import React, { type ReactNode } from 'react';
import { Modal } from '../../../shared/modals/Modal';
import {
  Platform,
  Pressable,
  ScrollView,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { colors } from '../../../shared/theme/colors'
import { spacing } from '../../../shared/theme/spacing';
import { REPORTS_VENTAS_WIDE_BREAKPOINT } from '../reportsVentasLayout';
import { styles } from '../ReportsScreen.styles';
import { ticketColors } from '../../../shared/theme/ticketColors';

type Props = {
  visible: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  /** Texto bajo el título (opcional). */
  hint?: string;
  /** Contenido fijo arriba del scroll (ej. buscador). */
  headerAccessory?: ReactNode;
  maxScrollHeight?: number;
};

/** Bottom sheet / panel centrado en web, mismo lenguaje que filtros de Ventas. */
export function ReportsScopeFilterSheet({
  visible,
  title,
  onClose,
  children,
  hint,
  headerAccessory,
  maxScrollHeight = 420,
}: Props) {
  const { width: windowWidth } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const isWebWideModal = Platform.OS === 'web' && windowWidth >= REPORTS_VENTAS_WIDE_BREAKPOINT;
  const sheetBottomPad = Math.max(insets.bottom, spacing.md);

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <Pressable
        style={[styles.reportsSalesListModalBackdrop, isWebWideModal && styles.reportsSalesListModalBackdropWeb]}
        onPress={onClose}
      >
        <Pressable
          style={[
            styles.reportsSalesListModalSheet,
            isWebWideModal && styles.reportsSalesListModalSheetWeb,
            { paddingBottom: sheetBottomPad },
          ]}
          onPress={(e) => e.stopPropagation()}
        >
          <View style={styles.reportsSalesListModalHeader}>
            <Text style={styles.reportsSalesListModalTitle}>{title}</Text>
            <TouchableOpacity
              onPress={onClose}
              accessibilityRole="button"
              accessibilityLabel="Cerrar"
              hitSlop={12}
            >
              <Ionicons name="close" size={26} color={ticketColors.ink} />
            </TouchableOpacity>
          </View>
          {hint ? (
            <Text style={[styles.caption, { color: ticketColors.ink3, marginBottom: spacing.sm }]}>{hint}</Text>
          ) : null}
          {headerAccessory}
          <ScrollView
            style={[styles.reportsSalesListModalScroll, { maxHeight: maxScrollHeight }]}
            keyboardShouldPersistTaps="handled"
          >
            {children}
          </ScrollView>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

type SheetRowProps = {
  label: string;
  selected: boolean;
  onPress: () => void;
  disabled?: boolean;
  subtitle?: string;
};

export function ReportsScopeFilterSheetRow({
  label,
  selected,
  onPress,
  disabled,
  subtitle,
}: SheetRowProps) {
  return (
    <TouchableOpacity
      style={[styles.reportsSalesListSheetRow, selected && styles.reportsSalesListSheetRowSelected]}
      onPress={onPress}
      disabled={disabled}
      accessibilityRole="button"
      accessibilityState={{ selected }}
      accessibilityLabel={label}
    >
      <View style={styles.reportsSalesListSheetRowText}>
        <Text
          style={[styles.reportsSalesListSheetRowTitle, selected && styles.reportsSalesListSheetRowTitleSelected]}
        >
          {label}
        </Text>
        {subtitle ? <Text style={styles.reportsSalesListSheetRowSubtitle}>{subtitle}</Text> : null}
      </View>
      {selected ? <Ionicons name="checkmark-circle" size={22} color={ticketColors.ok} /> : null}
    </TouchableOpacity>
  );
}
